export type TrackerGroupId = "upcoming" | "completed" | "changed";

export interface TrackerEvent {
  date: string;
  event: string;
  kind: string;
  status: string;
  statusLabel: string;
  source?: string;
  workstreamId: string;
  sources: string[];
  workstreamIds: string[];
  group: TrackerGroupId;
}

type TrackerEntry = Record<string, unknown>;
type TrackersMap = Record<string, Record<string, TrackerEntry[]>>;

const COMPLETED = /\b(done|signed|executed|completed|closed|received|delivered|filed|accepted|approved|resolved|satisfied|issued|countersigned|paid|occurred|held|performed|joined|left|took over|replaced)\b/i;
const CHANGED = /\b(changed|rescheduled|moved|postponed|cancelled|canceled|superseded|withdrawn|rejected)\b/i;
const UPCOMING = /\b(upcoming|pending|scheduled|expected|target|due|deadline|open|outstanding|requested|planned)\b/i;

function text(value: unknown): string {
  return typeof value === "string" ? value.trim() : value == null ? "" : String(value).trim();
}

function inferGroup(entry: TrackerEntry): TrackerGroupId {
  const status = text(entry.status);
  const kind = text(entry.kind);
  const event = text(entry.event);
  const combined = `${status} ${kind} ${event}`;
  if (CHANGED.test(combined)) return "changed";
  if (COMPLETED.test(status)) return "completed";
  if (UPCOMING.test(status)) return "upcoming";
  if (COMPLETED.test(`${kind} ${event}`)) return "completed";
  if (UPCOMING.test(`${kind} ${event}`) || /\bdeadline\b/i.test(kind)) return "upcoming";
  if (/\bmilestone\b/i.test(kind)) return "completed";
  return "upcoming";
}

function labelFor(entry: TrackerEntry, group: TrackerGroupId): string {
  const combined = `${text(entry.status)} ${text(entry.event)}`;
  if (/\brescheduled|moved|postponed\b/i.test(combined)) return "Rescheduled";
  if (/\bcancelled|canceled\b/i.test(combined)) return "Cancelled";
  if (/\bsuperseded\b/i.test(combined)) return "Superseded";
  if (/\brejected|withdrawn\b/i.test(combined)) return "Changed";
  if (group === "completed") return "Done";
  if (/\bscheduled\b/i.test(combined)) return "Scheduled";
  if (/\bexpected|target\b/i.test(combined)) return "Expected";
  return "Pending";
}

function dateKey(value: string): number {
  const iso = value.match(/^(\d{4})-(\d{2})(?:-(\d{2}))?/);
  if (!iso) return Number.NaN;
  return Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3] || "1"));
}

export function formatTrackerSources(sources: string[]): string {
  const emailNumbers: string[] = [];
  sources.forEach((source) => {
    for (const match of source.matchAll(/\bEmail\s*#\s*(\d+)\b/gi)) {
      if (!emailNumbers.includes(match[1])) emailNumbers.push(match[1]);
    }
  });
  if (emailNumbers.length === 1) return `Email #${emailNumbers[0]}`;
  if (emailNumbers.length > 1) return `Emails ${emailNumbers.map((number) => `#${number}`).join(", ")}`;
  return "Source unavailable";
}

export function formatTrackerDate(value: string): string {
  const iso = value.match(/^(\d{4})-(\d{2})(?:-(\d{2}))?/);
  if (!iso) return value || "Date not specified";
  if (!iso[3]) {
    return new Intl.DateTimeFormat("en", { month: "long", year: "numeric", timeZone: "UTC" })
      .format(new Date(Date.UTC(Number(iso[1]), Number(iso[2]) - 1, 1)));
  }
  return new Intl.DateTimeFormat("en", { day: "numeric", month: "short", year: "numeric", timeZone: "UTC" })
    .format(new Date(Date.UTC(Number(iso[1]), Number(iso[2]) - 1, Number(iso[3]))).getTime())
    .toUpperCase();
}

/** The Tracker screen is a date/event view. Other tracker state (issues,
 * diligence, signatures, wires, etc.) still powers the matter and checks, but
 * only universal-timeline entries belong in this concise presentation. */
export function trackerEvents(trackers: TrackersMap): TrackerEvent[] {
  const canonical = new Map<string, TrackerEvent>();
  Object.entries(trackers).forEach(([workstreamId, state]) => {
    const timeline = Array.isArray(state.timeline) ? state.timeline : [];
    timeline.forEach((entry) => {
      const date = text(entry.date);
      const event = text(entry.event);
      if (!date || !event) return;
      const group = inferGroup(entry);
      const source = text(entry.source) || undefined;
      const storedSources = Array.isArray(entry.sources) ? entry.sources.map(text).filter(Boolean) : [];
      const historySources = Array.isArray(entry.history)
        ? entry.history.map((item) => text((item as TrackerEntry)?.source)).filter(Boolean)
        : [];
      const sources = [...new Set([...storedSources, ...historySources, ...(source ? [source] : [])])];
      const key = `${date.toLowerCase()}|${event.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim()}`;
      const existing = canonical.get(key);
      if (existing) {
        existing.workstreamIds = [...new Set([...existing.workstreamIds, workstreamId])];
        existing.sources = [...new Set([...existing.sources, ...sources])];
        existing.source = formatTrackerSources(existing.sources);
        return;
      }
      canonical.set(key, {
        date,
        event,
        kind: text(entry.kind),
        status: text(entry.status),
        statusLabel: labelFor(entry, group),
        source: formatTrackerSources(sources),
        workstreamId,
        sources,
        workstreamIds: [workstreamId],
        group,
      });
    });
  });
  const events = [...canonical.values()];
  return events.sort((a, b) => {
    const aDate = dateKey(a.date);
    const bDate = dateKey(b.date);
    if (Number.isNaN(aDate) && Number.isNaN(bDate)) return b.date.localeCompare(a.date);
    if (Number.isNaN(aDate)) return 1;
    if (Number.isNaN(bDate)) return -1;
    return bDate - aDate;
  });
}
